import { api } from '@/api/client'
import {
  dayInLocalTz,
  db,
  type CycleRow,
  type RecoveryRow,
  type SleepRow,
  type WorkoutRow,
} from './schema'
import type { Cycle, Recovery, Sleep, Workout } from '@/api/types'

export type SyncPhase = 'idle' | 'profile' | 'cycles' | 'recoveries' | 'sleep' | 'workouts' | 'done' | 'error'

export interface SyncProgress {
  phase: SyncPhase
  message: string
  counts: { cycles: number; recoveries: number; sleep: number; workouts: number }
}

type ProgressFn = (p: SyncProgress) => void

const initialCounts = (): SyncProgress['counts'] => ({
  cycles: 0,
  recoveries: 0,
  sleep: 0,
  workouts: 0,
})

/**
 * Backfill window for incremental syncs — we re-pull this much history each
 * time so updated/late-scoring records get refreshed.
 */
const INCREMENTAL_OVERLAP_DAYS = 3

export async function syncAll(onProgress?: ProgressFn): Promise<SyncProgress> {
  const counts = initialCounts()
  const emit = (phase: SyncPhase, message: string) => {
    onProgress?.({ phase, message, counts: { ...counts } })
  }

  try {
    emit('profile', 'Getting started…')
    const [profile, body] = await Promise.all([api.profile(), api.body()])
    await db.profile.put({ ...profile, id: 'me', fetched_at: new Date().toISOString() })
    await db.body.put({ ...body, id: 'me', fetched_at: new Date().toISOString() })

    emit('cycles', 'Loading your activity…')
    counts.cycles = await syncResource<Cycle, CycleRow>(
      'cycle',
      api.cycles,
      (c) => ({
        ...c,
        // Whoop cycles run from sleep onset to next sleep onset. So `start`
        // is the previous evening; the awake day the user actually
        // associates with the cycle is when it ENDS. For the active cycle
        // (no end), that's today.
        day: dayInLocalTz(c.end ?? new Date().toISOString(), c.timezone_offset),
      }),
      (rows) => db.cycles.bulkPut(rows),
      () => emit('cycles', 'Loading your activity…'),
    )

    emit('recoveries', 'Loading recovery scores…')
    counts.recoveries = await syncResource<Recovery, RecoveryRow>(
      'recovery',
      api.recoveries,
      (r) => ({
        ...r,
        // Use the matching cycle's day if present; fall back to created_at.
        day: r.created_at.slice(0, 10),
      }),
      (rows) => db.recoveries.bulkPut(rows),
      () => emit('recoveries', 'Loading recovery scores…'),
    )

    emit('sleep', 'Loading sleep…')
    counts.sleep = await syncResource<Sleep, SleepRow>(
      'sleep',
      api.sleep,
      (s) => ({
        ...s,
        day: dayInLocalTz(s.end, s.timezone_offset),
      }),
      (rows) => db.sleep.bulkPut(rows),
      () => emit('sleep', 'Loading sleep…'),
    )

    emit('workouts', 'Loading your workouts…')
    counts.workouts = await syncResource<Workout, WorkoutRow>(
      'workout',
      api.workouts,
      (w) => ({
        ...w,
        day: dayInLocalTz(w.start, w.timezone_offset),
      }),
      (rows) => db.workouts.bulkPut(rows),
      () => emit('workouts', 'Loading your workouts…'),
    )

    emit('done', 'Up to date')
    return { phase: 'done', message: 'Up to date', counts: { ...counts } }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    emit('error', msg)
    return { phase: 'error', message: msg, counts: { ...counts } }
  }
}

async function syncResource<T extends { start?: string; created_at: string; updated_at: string }, R>(
  resource: 'cycle' | 'recovery' | 'sleep' | 'workout',
  iter: (p: { start?: string; end?: string; limit?: number }) => AsyncIterable<T>,
  toRow: (t: T) => R,
  put: (rows: R[]) => Promise<unknown>,
  onCount: (n: number) => void,
): Promise<number> {
  const cursor = await db.cursors.get(resource)
  const start = cursor?.high_water_iso
    ? subtractDays(cursor.high_water_iso, INCREMENTAL_OVERLAP_DAYS)
    : undefined

  const buffer: R[] = []
  let total = 0
  let highWater = cursor?.high_water_iso ?? null

  for await (const rec of iter({ start, limit: 25 })) {
    buffer.push(toRow(rec))
    total++
    const t = (rec.start ?? rec.created_at)
    if (!highWater || t > highWater) highWater = t

    if (buffer.length >= 200) {
      await put(buffer.splice(0, buffer.length))
      onCount(total)
    }
  }
  if (buffer.length) await put(buffer)
  onCount(total)

  await db.cursors.put({
    resource,
    high_water_iso: highWater,
    last_run_iso: new Date().toISOString(),
  })
  return total
}

function subtractDays(iso: string, days: number): string {
  const d = new Date(iso)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString()
}
